﻿import { z } from 'zod';
import type { CannonRarity, Rarity } from './rarity';

/**
 * Typed content catalog.
 *
 * Static, hand-authored truth about which asset belongs to which piece of
 * content. File paths are relative to the generated `game-assets` root; the
 * master files live in `assets-source` and are never referenced directly by
 * the game at runtime.
 */

/* -------------------------------------------------------------------------- */
/*  Maps                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Fifty maps: twenty planned for the closed alpha, thirty reserved after it.
 *
 * ## Three different questions, three different fields
 *
 * These were one field once - `status: 'playable' | 'locked_future'` - and that
 * conflated things that are genuinely independent:
 *
 * - **Planned for the closed alpha** is a roadmap fact about maps 1 to 20. It
 *   does not move.
 * - **Future reserved** is maps 21 to 50, which are not part of the alpha at
 *   all.
 *
 * ## `contentReady` viveu aqui, e saiu (gate V28I.BQ)
 *
 * Havia um terceiro campo, calculado por `mapIndex <= CONTENT_READY_THROUGH_MAP`
 * com a constante fixada em 1. Ele nunca portou nada: nenhuma recusa o lia,
 * nenhum botão o consultava, e a viagem já autorizava os vinte mapas. Era uma
 * etiqueta que dizia "só o mapa 1 está pronto" enquanto o jogo deixava entrar
 * nos vinte — e depois dos gates V28I.BN e V28I.BP a frase deixou de ser sequer
 * verdadeira.
 *
 * A disponibilidade passou a ser DERIVADA das fontes que já decidem o que
 * existe: `huntMapIsPlayable`, em `@kenocyber/game-domain`. Este catálogo
 * continua a publicar os factos — planeado, reservado, e que arte pertence a
 * cada mapa — e deixou de publicar um juízo que não podia formar sozinho: a
 * curva económica e os limites da simulação vivem uma camada acima.
 *
 * A interface continua a poder dizer "desbloqueado, em produção": o servidor
 * calcula o predicado e envia-o em `contentReady` na lista de mapas, com a
 * mesma forma de fio que tinha.
 */
export const CLOSED_ALPHA_MAP_COUNT = 20;
export const FUTURE_MAP_COUNT = 30;
export const TOTAL_MAP_COUNT = CLOSED_ALPHA_MAP_COUNT + FUTURE_MAP_COUNT;

/**
 * The old name for `CLOSED_ALPHA_MAP_COUNT`, kept for its existing callers.
 *
 * An alias, deliberately derived rather than a second literal: the asset
 * pipeline and the hunt balance both read it, and two numbers that must agree
 * eventually stop agreeing. "Playable" was also the wrong word - these twenty
 * are PLANNED, and nineteen of them are not playable at all.
 */
export const PLAYABLE_MAP_COUNT = CLOSED_ALPHA_MAP_COUNT;

/**
 * The old name for `FUTURE_MAP_COUNT`, kept for its existing callers.
 *
 * Derived for the same reason: `game-domain/src/maps.ts` re-exports it, so
 * deleting it would break a public name for no gain, and duplicating the
 * literal would create the second source this whole change exists to remove.
 */
export const LOCKED_MAP_COUNT = FUTURE_MAP_COUNT;

export interface MapCatalogEntry {
  /**
   * 1-based map index: the AUTHORITATIVE identity.
   *
   * This is what `player_progression.current_map`, `hunt_sessions.map_index`,
   * the snapshot, the offline fingerprint and the Power profiles all key on.
   * Nothing persists anything else.
   */
  readonly mapIndex: number;
  /**
   * `map_01` … `map_50`, DERIVED for the wire and the interface.
   *
   * Never stored, never accepted as input, never a second identity - it is a
   * presentation of `mapIndex` and can always be recomputed from it.
   */
  readonly mapCode: string;
  readonly status: 'playable' | 'locked_future';
  /** True for maps 1 to 20: planned for the closed alpha. */
  readonly plannedForClosedAlpha: boolean;
  /** True for maps 21 to 50: reserved for content after the alpha. */
  readonly futureReserved: boolean;
  /** Asset ids in the generated manifest; null for maps without art. */
  readonly mobAssetId: string | null;
  readonly bossAssetId: string | null;
  /**
   * O pano de fundo deste mapa, `null` quando não existe (gate V28I.BQ).
   *
   * Derivado como os outros dois e a partir da mesma regra: o pipeline emite
   * `background1..20` da mesma contagem, e a cena consumia isto por uma função
   * própria em `apps/web` que repetia a derivação. Uma segunda derivação é uma
   * segunda oportunidade de discordar, e a disponibilidade de um mapa precisa
   * de saber se ele tem fundo sem perguntar ao navegador.
   */
  readonly backgroundAssetId: string | null;
}

/** `map_01` … `map_50`. Zero-padded so codes sort the way they read. */
export function mapCodeForIndex(mapIndex: number): string {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > TOTAL_MAP_COUNT) {
    throw new RangeError(`mapIndex must be an integer in [1, ${TOTAL_MAP_COUNT}], got ${mapIndex}`);
  }
  return `map_${String(mapIndex).padStart(2, '0')}`;
}

/** alienN + bossN + backgroundN belong to map N, for 1..20. 21..50 have no art. */
export function mapCatalogEntry(mapIndex: number): MapCatalogEntry {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > TOTAL_MAP_COUNT) {
    throw new RangeError(`mapIndex must be an integer in [1, ${TOTAL_MAP_COUNT}], got ${mapIndex}`);
  }
  const plannedForClosedAlpha = mapIndex <= CLOSED_ALPHA_MAP_COUNT;
  return {
    mapIndex,
    mapCode: mapCodeForIndex(mapIndex),
    status: plannedForClosedAlpha ? 'playable' : 'locked_future',
    plannedForClosedAlpha,
    futureReserved: !plannedForClosedAlpha,
    // The asset ids of the planned twenty are untouched: the pipeline already
    // resolves them, and a map that is not content-ready simply has no art
    // behind the id yet. Blanking them here would break that resolution.
    mobAssetId: plannedForClosedAlpha ? `alien${mapIndex}` : null,
    bossAssetId: plannedForClosedAlpha ? `boss${mapIndex}` : null,
    backgroundAssetId: plannedForClosedAlpha ? `background${mapIndex}` : null,
  };
}

/**
 * A human label for a map, DERIVED from its index.
 *
 * Provisional on purpose. The twenty maps have no designed names yet, and
 * inventing them here would be filling in a pending decision on my own
 * initiative - so this is a presentation of the index, exactly like
 * `mapCode`, and can be replaced the day real names exist without anything
 * having stored one.
 */
export function mapDisplayName(mapIndex: number): string {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > TOTAL_MAP_COUNT) {
    throw new RangeError(`mapIndex must be an integer in [1, ${TOTAL_MAP_COUNT}], got ${mapIndex}`);
  }
  return `Setor ${String(mapIndex).padStart(2, '0')}`;
}

export const MAP_CATALOG: readonly MapCatalogEntry[] = Object.freeze(
  Array.from({ length: TOTAL_MAP_COUNT }, (_, i) => mapCatalogEntry(i + 1)),
);

/* -------------------------------------------------------------------------- */
/*  Ships                                                                     */
/* -------------------------------------------------------------------------- */

export const SHIP_COUNT = 20;

/**
 * Level-1 damage bonus in basis points, sequential: two ships per rarity.
 * These mirror the decided values in `docs/GAME_DESIGN.md`.
 */
const SHIP_RARITY_SEQUENCE: readonly Rarity[] = Object.freeze([
  'common',
  'uncommon',
  'rare',
  'epic',
  'mythic',
  'legendary',
  'excellent',
  'perfect',
  'anomaly',
  'prismatic',
]);

export interface ShipCatalogEntry {
  /** 1-based ship index; asset id is `ship{index}`. */
  readonly shipIndex: number;
  readonly assetId: string;
  readonly rarity: Rarity;
  /**
   * Damage bonus at level 1, in basis points. Ships grant ONLY a percentage
   * damage bonus - never health, never attack speed.
   */
  readonly level1DamageBonusBps: number;
}

/**
 * Level-1 ship damage bonus per rarity, in basis points. Single source of
 * truth; `@kenocyber/game-domain` re-exports this table.
 */
export const SHIP_LEVEL_1_DAMAGE_BONUS_BPS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 100,
  uncommon: 200,
  rare: 400,
  epic: 600,
  mythic: 900,
  legendary: 1200,
  excellent: 1600,
  perfect: 2000,
  anomaly: 2500,
  prismatic: 3300,
});

export function shipCatalogEntry(shipIndex: number): ShipCatalogEntry {
  if (!Number.isSafeInteger(shipIndex) || shipIndex < 1 || shipIndex > SHIP_COUNT) {
    throw new RangeError(`shipIndex must be an integer in [1, ${SHIP_COUNT}], got ${shipIndex}`);
  }
  const rarity = SHIP_RARITY_SEQUENCE[Math.floor((shipIndex - 1) / 2)];
  if (!rarity) {
    throw new RangeError(`no rarity mapped for ship ${shipIndex}`);
  }
  return {
    shipIndex,
    assetId: `ship${shipIndex}`,
    rarity,
    level1DamageBonusBps: SHIP_LEVEL_1_DAMAGE_BONUS_BPS[rarity],
  };
}

export const SHIP_CATALOG: readonly ShipCatalogEntry[] = Object.freeze(
  Array.from({ length: SHIP_COUNT }, (_, i) => shipCatalogEntry(i + 1)),
);

export const MAX_SHIP_LEVEL_CATALOG = 10;

/**
 * Ship bonus at level L: `level1Bonus x 2^(L-1)`. Max level 10.
 * Future upgrades merge two identical copies of the same level.
 */
export function shipDamageBonusBpsAtLevel(shipIndex: number, level: number): number {
  if (!Number.isSafeInteger(level) || level < 1 || level > MAX_SHIP_LEVEL_CATALOG) {
    throw new RangeError(`ship level must be in [1, ${MAX_SHIP_LEVEL_CATALOG}], got ${level}`);
  }
  return shipCatalogEntry(shipIndex).level1DamageBonusBps * 2 ** (level - 1);
}

/* -------------------------------------------------------------------------- */
/*  Cannons                                                                   */
/* -------------------------------------------------------------------------- */

export interface CannonCatalogEntry {
  readonly rarity: CannonRarity;
  /**
   * Asset id in the generated manifest. Never null: every cannon that exists
   * has its own art. The nullable case existed only for the planned fourth
   * cannon, whose update was cancelled (ADR 0132).
   */
  readonly assetId: string;
  /** Projectiles fired per cannon per volley. Cannons never change damage. */
  readonly projectilesPerCannon: number;
  /**
   * Neon projectile tint, part of the equipment configuration - the renderer
   * must read it from here, never hard-code it.
   */
  readonly projectileColor: string;
}

export const CANNON_CATALOG: Readonly<Record<CannonRarity, CannonCatalogEntry>> = Object.freeze({
  excellent: {
    rarity: 'excellent',
    assetId: 'cannon_excellent',
    projectilesPerCannon: 1,
    projectileColor: '#2F9BFF',
  },
  perfect: {
    rarity: 'perfect',
    assetId: 'cannon_perfect',
    projectilesPerCannon: 2,
    projectileColor: '#FFE81F',
  },
  anomaly: {
    rarity: 'anomaly',
    assetId: 'cannon_anomaly',
    projectilesPerCannon: 3,
    projectileColor: '#FF2222',
  },
});

/**
 * **The widest volley the catalogue admits**, in projectiles.
 *
 * Derived, never written down: the most projectiles one cannon emits, times
 * the most cannons a hull carries. Today that is the Anomaly at three, times
 * seven — twenty-one. It was twenty-eight while a fourth cannon of four
 * projectiles was planned; that update was cancelled (ADR 0132), and the
 * number moved on its own because nobody had spelled it out.
 */
export const MAX_PROJECTILES_PER_CANNON = Math.max(
  ...Object.values(CANNON_CATALOG).map((entry) => entry.projectilesPerCannon),
);

/* -------------------------------------------------------------------------- */
/*  Items                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Item families. Catalogued only: effects, drops and inventory are later
 * phases. The image index is NOT assumed to mean rarity, level or map.
 */
export const ITEM_FAMILIES = ['damage', 'life', 'heal'] as const;
export type ItemFamily = (typeof ITEM_FAMILIES)[number];

export const ITEM_FAMILY_INFO: Readonly<
  Record<ItemFamily, { readonly description: string; readonly count: number }>
> = Object.freeze({
  damage: { description: 'damage bonus items (effects pending design)', count: 20 },
  life: { description: 'max life bonus items (effects pending design)', count: 20 },
  heal: { description: 'healing potions (effects pending design)', count: 20 },
});

export interface ItemCatalogEntry {
  readonly family: ItemFamily;
  /** 1-based index within the family. Meaning of the index: PENDING. */
  readonly index: number;
  readonly assetId: string;
}

export function itemCatalogEntry(family: ItemFamily, index: number): ItemCatalogEntry {
  const info = ITEM_FAMILY_INFO[family];
  if (!Number.isSafeInteger(index) || index < 1 || index > info.count) {
    throw new RangeError(`item index must be in [1, ${info.count}], got ${index}`);
  }
  return { family, index, assetId: `${family}_${index}` };
}

export const ITEM_CATALOG: readonly ItemCatalogEntry[] = Object.freeze(
  ITEM_FAMILIES.flatMap((family) =>
    Array.from({ length: ITEM_FAMILY_INFO[family].count }, (_, i) =>
      itemCatalogEntry(family, i + 1),
    ),
  ),
);

/* -------------------------------------------------------------------------- */
/*  Manifest lookup helper                                                    */
/* -------------------------------------------------------------------------- */

export const manifestAssetIdSchema = z.string().min(1);
